import { useEffect } from 'react';
import { useLegalSettings, orPlaceholder } from '../hooks/useLegalSettings';

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { settings, loading } = useLegalSettings();
  const s = settings || {};
  const updateLabel = s.updatedAtLabel || '—';
  const email = s.email || 'contact@pernetyjewelry.com';

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />
        <div className="relative z-10 text-center px-6">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-12 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-10 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-12 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(36px, 5vw, 60px)'
          }}>
            Politique de confidentialité
          </h1>
        </div>
      </section>

      {/* ── CONTENU ── */}
      <section className="bg-white py-20 md:py-24 px-6">
        <div className="max-w-3xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>

          <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-12 text-center">
            Dernière mise à jour : {updateLabel}
          </p>

          {loading ? (
            <p className="text-center text-gray-400">Chargement...</p>
          ) : (
          <>

          {/* Préambule */}
          <Block title="Préambule">
            <p>
              La présente politique de confidentialité a pour objet de vous informer, de manière transparente, sur la manière dont Pernety collecte, utilise et protège vos données à caractère personnel lorsque vous visitez le site <strong>pernetyparis.com</strong> ou interagissez avec nous.
            </p>
            <p>
              Cette politique est conforme au Règlement (UE) 2016/679 du Parlement européen et du Conseil du 27 avril 2016 (« RGPD ») ainsi qu'à la loi française « Informatique et Libertés » modifiée du 6 janvier 1978.
            </p>
          </Block>

          {/* Responsable */}
          <Block title="1. Responsable du traitement">
            <p>
              Le responsable du traitement de vos données est :
            </p>
            <ul className="space-y-1 my-4">
              <li><strong>Identité :</strong> {orPlaceholder(s.legalName, 'Prénom NOM')} / Pernety</li>
              <li><strong>Statut :</strong> {orPlaceholder(s.legalStatus, 'ex. Entrepreneur individuel')}</li>
              <li><strong>Adresse :</strong> {orPlaceholder(s.address, 'adresse complète')}</li>
              <li><strong>SIRET :</strong> {orPlaceholder(s.siret, '14 chiffres')}</li>
              <li><strong>Email :</strong> {email}</li>
              <li><strong>Téléphone :</strong> {orPlaceholder(s.phone, 'numéro de téléphone')}</li>
            </ul>
            <p>
              Pernety n'a pas désigné de Délégué à la Protection des Données (DPO) car la nomination n'est pas obligatoire au regard de l'activité et du volume de données traitées. Toute demande relative à vos données personnelles peut être adressée directement au responsable du traitement aux coordonnées ci-dessus.
            </p>
          </Block>

          {/* Données collectées */}
          <Block title="2. Données personnelles collectées">
            <p>Les données suivantes peuvent être collectées sur le site :</p>

            <h3 className="font-medium text-gray-800 mt-6 mb-2">2.1 Via le formulaire de contact</h3>
            <ul className="space-y-1 my-2">
              <li>— Nom et prénom</li>
              <li>— Adresse email</li>
              <li>— Numéro de téléphone (facultatif)</li>
              <li>— Type de demande (renseignement, commande sur mesure, prise de rendez-vous, autre)</li>
              <li>— Type de bijou souhaité, budget envisagé, occasion (facultatifs)</li>
              <li>— Contenu du message</li>
            </ul>

            <h3 className="font-medium text-gray-800 mt-6 mb-2">2.2 Données techniques</h3>
            <ul className="space-y-1 my-2">
              <li>— Adresse IP (collectée par les logs du serveur à des fins de sécurité)</li>
              <li>— Type de navigateur et système d'exploitation</li>
              <li>— Pages visitées et date/heure de visite</li>
            </ul>

            <p className="mt-4">
              Aucune donnée sensible au sens de l'article 9 du RGPD (origine raciale ou ethnique, opinions politiques, convictions religieuses, données de santé, etc.) n'est collectée.
            </p>
          </Block>

          {/* Finalités */}
          <Block title="3. Finalités du traitement">
            <p>Vos données sont collectées et traitées pour les finalités suivantes :</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li>Répondre aux demandes de contact, de devis ou de prise de rendez-vous adressées via le formulaire</li>
              <li>Gérer la relation client et les éventuelles commandes sur mesure</li>
              <li>Assurer la sécurité, la disponibilité et le bon fonctionnement du site</li>
              <li>Respecter nos obligations légales et réglementaires (notamment comptables et fiscales)</li>
            </ul>
            <p>
              Vos données ne font l'objet d'aucune décision automatisée ni de profilage.
            </p>
          </Block>

          {/* Bases légales */}
          <Block title="4. Bases légales du traitement">
            <p>
              Conformément à l'article 6 du RGPD, le traitement de vos données repose sur les bases légales suivantes :
            </p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>Consentement</strong> (art. 6.1.a) : lorsque vous remplissez et soumettez le formulaire de contact</li>
              <li><strong>Exécution d'un contrat ou de mesures précontractuelles</strong> (art. 6.1.b) : pour donner suite à vos demandes de devis ou de commande</li>
              <li><strong>Intérêt légitime</strong> (art. 6.1.f) : pour assurer la sécurité du site et de notre système d'information</li>
              <li><strong>Obligation légale</strong> (art. 6.1.c) : pour respecter les durées de conservation imposées par la loi (notamment en matière comptable)</li>
            </ul>
          </Block>

          {/* Durée de conservation */}
          <Block title="5. Durée de conservation">
            <p>Vos données sont conservées pendant les durées suivantes :</p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>Messages de contact :</strong> 3 ans à compter du dernier échange, conformément aux recommandations de la CNIL en matière de prospection</li>
              <li><strong>Données de clients (commandes) :</strong> 5 ans après la fin de la relation contractuelle, en conformité avec les obligations comptables (art. L.123-22 du Code de commerce)</li>
              <li><strong>Logs de connexion :</strong> 12 mois maximum (recommandation CNIL)</li>
            </ul>
            <p>
              Au-delà de ces durées, les données sont soit supprimées, soit anonymisées à des fins statistiques.
            </p>
          </Block>

          {/* Destinataires */}
          <Block title="6. Destinataires de vos données">
            <p>
              Vos données sont destinées exclusivement à Pernety et ne sont en aucun cas vendues, louées ou cédées à des tiers à des fins commerciales.
            </p>
            <p className="mt-3">
              Elles peuvent toutefois être communiquées aux destinataires suivants, dans la limite stricte de leurs missions :
            </p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>Sous-traitants techniques</strong> agissant sur instruction du responsable du traitement, dans le cadre d'un contrat conforme à l'article 28 du RGPD :
                <ul className="space-y-1 my-2 list-[circle] pl-5">
                  <li><strong>OVH SAS</strong> (hébergement du serveur) — France</li>
                  <li><strong>MongoDB Atlas</strong> (hébergement de la base de données) — {orPlaceholder(s.mongoRegion, 'région d\'hébergement : UE/Irlande, ou hors UE')}</li>
                </ul>
              </li>
              <li><strong>Autorités administratives ou judiciaires</strong>, sur réquisition légale</li>
            </ul>
          </Block>

          {/* Transferts hors UE */}
          <Block title="7. Transferts de données hors Union européenne">
            <p>
              Si la base de données MongoDB Atlas est hébergée dans un pays situé hors de l'Union européenne, ce transfert est encadré par les <strong>Clauses Contractuelles Types</strong> approuvées par la Commission européenne, garantissant un niveau de protection équivalent au RGPD.
            </p>
            <p>
              Le site charge également des polices typographiques depuis Google Fonts (Playfair Display, Cormorant Garamond). Cela peut entraîner la transmission de votre adresse IP aux serveurs de Google LLC. Ces transferts sont encadrés par le <strong>Data Privacy Framework</strong> (DPF) entre l'UE et les États-Unis.
            </p>
          </Block>

          {/* Droits */}
          <Block title="8. Vos droits">
            <p>
              Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants sur vos données personnelles :
            </p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li><strong>Droit d'accès</strong> : obtenir la confirmation que vos données sont traitées et en obtenir une copie</li>
              <li><strong>Droit de rectification</strong> : faire corriger des données inexactes ou incomplètes</li>
              <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») : faire supprimer vos données dans les cas prévus par la loi</li>
              <li><strong>Droit à la limitation du traitement</strong> : suspendre temporairement le traitement de vos données</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement pour des motifs tenant à votre situation particulière</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré et lisible par machine</li>
              <li><strong>Droit de définir des directives post-mortem</strong> sur le sort de vos données après votre décès</li>
              <li><strong>Droit de retirer votre consentement</strong> à tout moment, sans que cela n'affecte la licéité du traitement antérieur</li>
            </ul>
            <p>
              Pour exercer ces droits, vous pouvez nous contacter à l'adresse <a href={`mailto:${email}`} className="underline hover:text-black">{email}</a> en justifiant de votre identité. Nous nous engageons à répondre dans un délai d'un mois à compter de la réception de votre demande, conformément à l'article 12 du RGPD.
            </p>
          </Block>

          {/* Réclamation CNIL */}
          <Block title="9. Réclamation auprès de la CNIL">
            <p>
              Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous avez la possibilité d'adresser une réclamation à la Commission Nationale de l'Informatique et des Libertés (CNIL) :
            </p>
            <ul className="space-y-1 my-4">
              <li><strong>Adresse :</strong> 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07</li>
              <li><strong>Téléphone :</strong> +33 (0)1 53 73 22 22</li>
              <li><strong>Site web :</strong> <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">www.cnil.fr</a></li>
            </ul>
          </Block>

          {/* Sécurité */}
          <Block title="10. Sécurité des données">
            <p>
              Pernety met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre la perte, la destruction, la divulgation, l'altération ou l'accès non autorisé, notamment :
            </p>
            <ul className="space-y-2 my-4 list-disc pl-5">
              <li>Chiffrement des communications via HTTPS (certificat SSL/TLS)</li>
              <li>Hashage cryptographique des mots de passe administrateur (bcrypt)</li>
              <li>Restriction des accès aux seules personnes autorisées</li>
              <li>Sauvegardes régulières et hébergement sur infrastructure sécurisée</li>
              <li>En-têtes de sécurité HTTP (Helmet)</li>
            </ul>
            <p>
              En cas de violation de données susceptible d'engendrer un risque élevé pour vos droits et libertés, vous serez informé conformément à l'article 34 du RGPD.
            </p>
          </Block>

          {/* Modifications */}
          <Block title="11. Modifications de la présente politique">
            <p>
              La présente politique peut être modifiée à tout moment, notamment pour s'adapter à l'évolution de la législation ou des activités de Pernety. La date de dernière mise à jour figure en tête du présent document. Nous vous invitons à la consulter régulièrement.
            </p>
          </Block>

          {/* Contact */}
          <Block title="12. Contact">
            <p>
              Pour toute question relative à la présente politique ou au traitement de vos données personnelles, vous pouvez nous écrire à l'adresse <a href={`mailto:${email}`} className="underline hover:text-black">{email}</a> ou via le <a href="/contact" className="underline hover:text-black">formulaire de contact</a>.
            </p>
            <p>
              Les <a href="/mentions-legales" className="underline hover:text-black">mentions légales</a> du site complètent la présente politique.
            </p>
          </Block>

          </>
          )}

        </div>
      </section>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mb-12">
      <h2 className="font-light mb-5 text-gray-900" style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 'clamp(20px, 1.8vw, 26px)'
      }}>
        {title}
      </h2>
      <div className="space-y-4 text-[14.5px] leading-[1.85]" style={{ color: '#4a4a4a' }}>
        {children}
      </div>
    </div>
  );
}
